import React from 'react';
import t from '@jetshop/intl';
import { css, cx } from 'linaria';
import { ELEVATE_SEARCH_ORIGINS } from '../../ElevateContext';
import { elevateAutocompleteTitleStyle } from './ElevateAutocompleteTitleStyle';
import { theme } from '../../../Theme';

function renderHighlightedText(highlighted, fallbackText) {
  const source = highlighted || fallbackText || '';

  // `highlighted` uses `{...}` to mark the matching part, e.g. "{ridby}xor fairfield"
  const parts = [];
  const re = /\{([^}]*)\}/g;
  let lastIndex = 0;
  let match;

  while ((match = re.exec(source)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: source.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'bold', value: match[1] ?? '' });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < source.length) {
    parts.push({ type: 'text', value: source.slice(lastIndex) });
  }

  // Uppercase the first character of the rendered label (matches previous behavior).
  for (const p of parts) {
    if (p.value) {
      p.value = p.value.charAt(0).toUpperCase() + p.value.slice(1);
      break;
    }
  }

  return parts.map((p, i) =>
    p.type === 'bold' ? (
      <strong key={i}>{p.value}</strong>
    ) : (
      <React.Fragment key={i}>{p.value}</React.Fragment>
    )
  );
}

const searchPhraseSuggestionsStyle = css`
  --button-light-grey: #e7e8e9;
  --button-dark-grey: #555;

  &.button-style {
    ul {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 8px;
      flex-wrap: wrap;
    }

    button {
      display: flex;
      padding: 6px 12px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border: none;
      background: var(--button-light-grey);
      color: var(--button-dark-grey);
      font-size: 14px;
      line-height: 1;
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      animation: fadeInScaleUp 0.3s ease-out forwards;
      opacity: 0;

      &:hover {
        background: var(--button-dark-grey);
        color: var(--button-light-grey);
        transform: translateY(-4px);
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05);
      }

      &:active {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      }
    }

    ul > button {
      animation-delay: calc(var(--idx, 0) * 50ms);
    }

    @keyframes fadeInScaleUp {
      from {
        opacity: 0;
        transform: scale(0.95) translateY(5px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }
  }
  &.text-style {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;

    ul {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      flex-wrap: wrap;
      ${theme.below.md} {
        gap: 8px;
      }
    }

    button {
      border: none;
      background: none;
      cursor: pointer;
      font-size: 16px;
      line-height: 142.857%;
      color: #191c1d;
      font-weight: 400;
      font-family: Figtree;
      padding: 0;
    }
  }
`;

export const ElevatePhraseSuggestions = ({
  title,
  suggestions,
  clickCallback,
  type = 'text' // text or button
}) => {
  return (
    <div
      className={cx(
        'search-phrase-suggestions',
        searchPhraseSuggestionsStyle,
        type === 'text' ? 'text-style' : 'button-style'
      )}
    >
      {title && <h2 className={elevateAutocompleteTitleStyle}>{t(title)}</h2>}
      <ul className="search-content-suggestions-presenter">
        {suggestions?.map((suggestion, index) => {
          const upperCasedFirstLetterSuggestion =
            suggestion?.q?.charAt(0)?.toUpperCase() + suggestion?.q?.slice(1);

          return (
            <button
              key={`${suggestion?.q}-${index}`}
              onClick={() => {
                clickCallback(upperCasedFirstLetterSuggestion, {
                  origin: ELEVATE_SEARCH_ORIGINS.phraseSuggest
                });
              }}
              style={{ '--idx': index, '--element-press-scale': '0.985' }}
            >
              {renderHighlightedText(
                suggestion?.highlighted,
                upperCasedFirstLetterSuggestion
              )}
            </button>
          );
        })}
      </ul>
    </div>
  );
};
